import { Box, type SxProps, type Theme } from "@mui/material";
import type { FC } from "react";
import { NEUTRAL } from "@/theme/tokens";

export type ColorDotSize = "xs" | "sm" | "md";

/** `circle` = a category. `diamond` = a variant inside a category (e.g. a one-off shift among recurring ones). */
export type ColorDotShape = "circle" | "diamond";

const SIZE_PX: Record<ColorDotSize, number> = { xs: 6, sm: 8, md: 10 };

/** Turns the square into a diamond. */
const DIAMOND_ROTATION = "rotate(45deg)";

/** Takes up exactly `footprint` × `footprint` px in layout, whichever shape is drawn, so the two shapes can be
    swapped in place without moving anything. The diamond is a smaller square turned 45°, with margins
    making up the rest of the box. The rotation does not affect layout, so its points land on the box edges. */
const shapeSx = (shape: ColorDotShape, footprint: number) => {
	if (shape === "circle") return { width: footprint, height: footprint, borderRadius: "50%" };
	// A 45°-turned square's diagonal is its side × √2, so a diamond that just fills the box has side footprint / √2.
	const side = footprint / Math.SQRT2;
	return { width: side, height: side, margin: `${(footprint - side) / 2}px`, transform: DIAMOND_ROTATION };
};

/**
 * The single categorical marker: a filled circle for "belongs to that colour's bucket", or a diamond for a
 * variant within that bucket. Replaces the hand-rolled `borderRadius: "50%"` Box each surface used to
 * write, which drifted in diameter so a band's dot and its list's dots didn't match.
 *
 * `aria-hidden` by default — the colour sits beside the label it encodes; pass `ariaLabel` only when
 * the dot is the sole statement of its category. Colour must be a resolved theme token, never a
 * call-site hex; with none it falls back to neutral so a missing category reads as unclassified.
 *
 * DEFAULT: `shape` is `circle` when omitted, so every existing caller keeps the circle it has always
 * drawn and a diamond is opt-in.
 */
export const ColorDot: FC<{
	/** Resolved colour token. Omitted → a muted neutral, i.e. "no category". */
	color?: string;
	/** `xs` 6px · `sm` 8px (default) · `md` 10px, or an explicit px footprint from a geometry token. A
	    border passed via `sx` does NOT add to that footprint. The app's `CssBaseline` makes every element
	    `border-box`, so the border is drawn inside the footprint and takes space from the fill. */
	size?: ColorDotSize | number;
	/** `circle` (default) or `diamond`. Both take up the same `size` footprint. */
	shape?: ColorDotShape;
	/** Makes the dot meaningful to assistive tech. Omit when an adjacent label already names the category. */
	ariaLabel?: string;
	sx?: SxProps<Theme>;
	dataTestId?: string;
}> = ({ color, size = "sm", shape = "circle", ariaLabel, sx, dataTestId }) => (
	<Box
		component="span"
		aria-hidden={ariaLabel ? undefined : true}
		aria-label={ariaLabel}
		role={ariaLabel ? "img" : undefined}
		data-testid={dataTestId}
		sx={[
			{
				flexShrink: 0,
				display: "inline-block",
				...shapeSx(shape, typeof size === "number" ? size : SIZE_PX[size]),
				backgroundColor: color ?? NEUTRAL[400],
			},
			...(Array.isArray(sx) ? sx : [sx]),
		]}
	/>
);

export default ColorDot;
